import { useMutation, useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { ParentShell } from '../components/ParentShell'
import { parentApi } from '../api/parent'
import { chatApi } from '../api/chat'
import { queryKeys } from '../api/queryKeys'

/** Детальная страница: оценки + посещаемость одного ребёнка (read-only). */
export function ParentChildPage() {
  const { t } = useTranslation()
  const { studentId = '' } = useParams<{ studentId: string }>()
  const [tab, setTab] = useState<'grades' | 'attendance'>('grades')

  const children = useQuery({ queryKey: queryKeys.parent.children(), queryFn: parentApi.myChildren, retry: 1 })
  const grades = useQuery({
    queryKey: queryKeys.parent.grades(studentId),
    queryFn: () => parentApi.childGrades(studentId),
    enabled: !!studentId && tab === 'grades',
  })
  const attendance = useQuery({
    queryKey: queryKeys.parent.attendance(studentId),
    queryFn: () => parentApi.childAttendance(studentId),
    enabled: !!studentId && tab === 'attendance',
  })

  const child = children.data?.find((c) => c.studentId === studentId)

  return (
    <ParentShell
      title={child?.fullName ?? t('parentChildPage.childFallback')}
      subtitle={child
        ? t('parentChildPage.subtitle', { className: child.className ?? '—', grade: child.gradeLevel ?? '?', n: child.gradesCount })
        : t('common.loading')}
    >
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <Link to="/parent" className="text-sm text-gray-500 hover:text-primary">
          ← {t('parentChildPage.backToList')}
        </Link>
        <ChatWithTeacherButton studentId={studentId} />
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-5 border-b border-gray-200">
        <button
          onClick={() => setTab('grades')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            tab === 'grades' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          📊 {t('parentChildPage.tabGrades')}
        </button>
        <button
          onClick={() => setTab('attendance')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            tab === 'attendance' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          ✅ {t('parentChildPage.tabAttendance')}
        </button>
      </div>

      {tab === 'grades' && (
        <>
          {grades.isLoading && <div className="text-center py-10 text-gray-500">{t('common.loading')}</div>}
          {grades.data?.length === 0 && (
            <div className="text-center py-12 text-gray-500">{t('parentChildPage.noGrades')}</div>
          )}
          {grades.data && grades.data.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto">
              <table className="w-full text-sm min-w-[560px]">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold text-gray-700">{t('parentChildPage.colDate')}</th>
                    <th className="text-left px-3 py-2 font-semibold text-gray-700">{t('parentChildPage.colSubject')}</th>
                    <th className="text-left px-3 py-2 font-semibold text-gray-700">{t('parentChildPage.colType')}</th>
                    <th className="text-center px-3 py-2 font-semibold text-gray-700">{t('parentChildPage.colGrade')}</th>
                    <th className="text-left px-3 py-2 font-semibold text-gray-700 hidden md:table-cell">{t('parentChildPage.colComment')}</th>
                  </tr>
                </thead>
                <tbody>
                  {grades.data.map((g) => (
                    <tr key={g.id} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="px-3 py-2 text-gray-700">{g.date}</td>
                      <td className="px-3 py-2 font-medium">{g.subjectName ?? '—'}</td>
                      <td className="px-3 py-2 text-xs text-gray-500">{gradeTypeLabel(g.type, t)}</td>
                      <td className="px-3 py-2 text-center">
                        <span className={`inline-block min-w-[24px] px-1.5 py-0.5 rounded text-sm font-bold ${gradeColor(g.value)}`}>
                          {g.value}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-gray-500 text-xs hidden md:table-cell">{g.comment ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {tab === 'attendance' && (
        <>
          {attendance.isLoading && <div className="text-center py-10 text-gray-500">{t('common.loading')}</div>}
          {attendance.data?.length === 0 && (
            <div className="text-center py-12 text-gray-500">{t('parentChildPage.noAttendance')}</div>
          )}
          {attendance.data && attendance.data.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto">
              <table className="w-full text-sm min-w-[560px]">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold text-gray-700">{t('parentChildPage.colDate')}</th>
                    <th className="text-left px-3 py-2 font-semibold text-gray-700">{t('parentChildPage.colStatus')}</th>
                    <th className="text-left px-3 py-2 font-semibold text-gray-700">{t('parentChildPage.colReason')}</th>
                  </tr>
                </thead>
                <tbody>
                  {attendance.data.map((a) => (
                    <tr key={a.id} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="px-3 py-2 text-gray-700">{a.date}</td>
                      <td className="px-3 py-2">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded ${markColor(a.mark)}`}>
                          {markLabel(a.mark, t)}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-gray-500">{a.reason ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </ParentShell>
  )
}

function ChatWithTeacherButton({ studentId }: { studentId: string }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const peers = useMutation({ mutationFn: () => chatApi.peersForStudent(studentId) })
  const open = useMutation({
    mutationFn: (peerId: string) => chatApi.openThread(peerId, studentId),
    onSuccess: ({ threadId }) => navigate(`/parent/chat?thread=${threadId}`),
  })

  if (!peers.data) {
    return (
      <button
        onClick={() => peers.mutate()}
        disabled={peers.isPending}
        className="btn-outline text-sm"
      >
        {peers.isPending ? t('parentChildPage.searchingTeachers') : `💬 ${t('parentChildPage.contactTeacher')}`}
      </button>
    )
  }
  if (peers.data.length === 0) {
    return <span className="text-xs text-gray-500">{t('parentChildPage.noTeachers')}</span>
  }
  if (peers.data.length === 1) {
    return (
      <button
        onClick={() => open.mutate(peers.data![0].id)}
        disabled={open.isPending}
        className="btn-primary text-sm"
      >
        💬 {t('parentChildPage.chatWith', { name: peers.data[0].name })}
      </button>
    )
  }
  return (
    <div className="flex gap-2 flex-wrap">
      {peers.data.map((p) => (
        <button
          key={p.id}
          onClick={() => open.mutate(p.id)}
          disabled={open.isPending}
          className="btn-outline text-sm"
        >
          💬 {p.name}
        </button>
      ))}
    </div>
  )
}

function gradeColor(value: string): string {
  const n = parseFloat(value)
  if (isNaN(n)) return 'bg-gray-100 text-gray-700'
  if (n >= 5) return 'bg-green-100 text-green-800'
  if (n >= 4) return 'bg-blue-100 text-blue-800'
  if (n >= 3) return 'bg-yellow-100 text-yellow-800'
  return 'bg-red-100 text-red-800'
}

function gradeTypeLabel(type: string, t: TFunction): string {
  return {
    current: t('parentChildPage.gradeTypeCurrent'),
    test: t('parentChildPage.gradeTypeTest'),
    exam: t('parentChildPage.gradeTypeExam'),
    project: t('parentChildPage.gradeTypeProject'),
    homework: t('parentChildPage.gradeTypeHomework'),
    term: t('parentChildPage.gradeTypeTerm'),
    year: t('parentChildPage.gradeTypeYear'),
  }[type] ?? type
}

function markLabel(m: string, t: TFunction): string {
  return {
    present: t('parentChildPage.markPresent'),
    late: t('parentChildPage.markLate'),
    absent: t('parentChildPage.markAbsent'),
    sick: t('parentChildPage.markSick'),
    excused: t('parentChildPage.markExcused'),
    unexcused: t('parentChildPage.markUnexcused'),
  }[m] ?? m
}

function markColor(m: string): string {
  return {
    present: 'bg-green-100 text-green-700',
    late: 'bg-yellow-100 text-yellow-700',
    absent: 'bg-red-100 text-red-700',
    sick: 'bg-blue-100 text-blue-700',
    excused: 'bg-violet-100 text-violet-700',
    unexcused: 'bg-gray-100 text-gray-700',
  }[m] ?? 'bg-gray-100 text-gray-700'
}
